import { Animation, createAnimation, TransitionOptions } from "@ionic/react";

/** Length of the onboarding fade, in ms. */
export const ONBOARDING_FADE_DURATION_MS = 300;

/**
 * Fade used for every onboarding navigation, forward and back.
 *
 * Ionic stacks both pages while it transitions and z-orders them by direction: going
 * forward the incoming page sits on top (101 over 100), going back the outgoing one
 * does (100 over 99). We dissolve whichever page is on top against the one behind it,
 * so the two screens blend directly into each other and the app background never shows
 * through.
 *
 * Going back, the page being revealed also has to be animated — Ionic leaves
 * `ion-page-invisible` (opacity: 0) on the entering page until *after* the transition
 * (see `afterTransition` in @ionic/core's utils/transition), and only an animation on
 * that element outranks the class. Holding it at opacity 1 is what stops the outgoing
 * screen dissolving into the bare app background instead of into the previous screen.
 *
 * Pass it per navigation — 5th argument of `push()`, 1st of `goBack()` — so only
 * onboarding fades. The rest of the app keeps the iOS slide set on the router outlet
 * in App.tsx.
 */
export const onboardingFadeTransition = (
  _baseEl: HTMLElement,
  opts?: TransitionOptions,
): Animation => {
  const root = createAnimation().duration(ONBOARDING_FADE_DURATION_MS).easing("ease-in-out");

  const enteringEl = opts?.enteringEl as HTMLElement | undefined;
  const leavingEl = opts?.leavingEl as HTMLElement | undefined;

  if (opts?.direction === "back") {
    if (!leavingEl) return root;
    if (enteringEl) {
      root.addAnimation(createAnimation().addElement(enteringEl).fromTo("opacity", 1, 1));
    }
    root.addAnimation(createAnimation().addElement(leavingEl).fromTo("opacity", 1, 0));
    return root;
  }

  if (!enteringEl) return root;
  root.addAnimation(createAnimation().addElement(enteringEl).fromTo("opacity", 0, 1));
  return root;
};
